import { useId } from "react";
import type { LucideIcon } from "lucide-react";
import { ArrowRight, Check, Clock, Loader2, Lock, TriangleAlert, UserRound } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import type { StageState } from "@/api/config-server-stages";
import { cn } from "@/lib/utils";

const STATE_LABEL: Record<StageState, string> = {
  locked: "Locked",
  not_started: "Not started",
  in_progress: "In progress",
  awaiting_you: "Waiting for you",
  done: "Completed",
};

// Fundos e bordas do design; o texto usa tons mais escuros que os do design onde
// o contraste do original ficaria abaixo de 4,5:1.
const STATE_STYLE: Record<StageState, string> = {
  locked: "border-[#e1e5eb] bg-[#f1f3f6] text-[var(--exed-ink-500)]",
  not_started: "border-[rgba(29,78,160,0.25)] bg-[rgba(29,78,160,0.1)] text-[#1d4ea0]",
  in_progress: "border-[rgba(255,200,61,0.45)] bg-[rgba(255,200,61,0.15)] text-[#7a5600]",
  awaiting_you: "border-[rgba(242,115,37,0.25)] bg-[rgba(242,115,37,0.1)] text-[#c2410c]",
  done: "border-[rgba(39,161,85,0.25)] bg-[rgba(39,161,85,0.1)] text-[#1e7f43]",
};

const STATE_ICON = {
  locked: Lock,
  not_started: Clock,
  in_progress: Loader2,
  awaiting_you: UserRound,
  done: Check,
} as const;

const ACCENT: Record<StageState, string> = {
  locked: "bg-[#c3cbd6]",
  not_started: "bg-[var(--exed-gold)]",
  in_progress: "bg-[var(--exed-gold)]",
  awaiting_you: "bg-[var(--exed-gold)]",
  done: "bg-[var(--exed-success)]",
};

const RING_RADIUS = 30;
const RING_LENGTH = 2 * Math.PI * RING_RADIUS;

const RING_COLOR: Record<StageState, string> = {
  locked: "#c3cbd6",
  not_started: "var(--exed-gold)",
  in_progress: "var(--exed-gold)",
  awaiting_you: "var(--exed-gold)",
  done: "var(--exed-success)",
};

const METRIC_LABEL = {
  neutral: "text-[var(--exed-ink-500)]",
  warn: "text-[#c2410c]",
  success: "text-[#1e7f43]",
} as const;

export interface StageMetric {
  label: string;
  value: string;
  /** Complemento ao lado do número, ex.: "of 152 tables read". */
  suffix?: string;
  tone?: keyof typeof METRIC_LABEL;
}

function clampValue(value: number): number {
  return Number.isNaN(value) ? 0 : Math.max(0, Math.min(100, Math.floor(value)));
}

function ProgressRing({ value, state }: { value: number; state: StageState }) {
  return (
    <div data-testid="stage-ring" className="relative h-[72px] w-[72px] shrink-0">
      <svg width="72" height="72" viewBox="0 0 72 72" aria-hidden="true" className="-rotate-90">
        <circle cx="36" cy="36" r={RING_RADIUS} fill="none" stroke="#eef1f5" strokeWidth="7" />
        {value > 0 && (
          <circle
            cx="36"
            cy="36"
            r={RING_RADIUS}
            fill="none"
            stroke={RING_COLOR[state]}
            strokeWidth="7"
            strokeLinecap="round"
            strokeDasharray={RING_LENGTH}
            strokeDashoffset={RING_LENGTH * (1 - value / 100)}
            className="transition-[stroke-dashoffset] duration-500 motion-reduce:transition-none"
          />
        )}
      </svg>
      <span
        className={cn(
          "absolute inset-0 flex items-center justify-center text-[15px] font-bold tabular-nums",
          state === "locked" ? "text-[var(--exed-ink-500)]" : state === "done" ? "text-[#1e7f43]" : "text-[var(--exed-ink-900)]",
        )}
      >
        {value}%
      </span>
    </div>
  );
}

const OVERLINE = "font-mono text-[11px] font-medium uppercase tracking-[0.14em]";

export function StageCard({
  number,
  title,
  description,
  state,
  value,
  metrics,
  note,
  progressLabel,
  lockedReason,
  lockedHint,
  actionLabel,
  actionIcon: ActionIcon,
  to,
}: {
  number: "01" | "02" | "03";
  title: string;
  description: string;
  state: StageState;
  /** 0–100 da própria etapa, vindo do overview. */
  value: number;
  /** Números grandes da etapa: um ocupa a linha, dois dividem em colunas. */
  metrics?: StageMetric[];
  /** Frase no lugar dos números, quando ainda não há o que contar. */
  note?: string;
  /** Legenda da mini-barra; sem ela a mini-barra não aparece. */
  progressLabel?: string;
  lockedReason?: string;
  lockedHint?: string;
  actionLabel: string;
  actionIcon?: LucideIcon;
  to: string;
}) {
  const { t } = useTranslation();
  const titleId = useId();
  const locked = state === "locked";
  const active = state === "awaiting_you" || state === "in_progress" || state === "not_started";
  const percent = clampValue(value);
  const Icon = STATE_ICON[state];

  return (
    <article
      aria-disabled={locked || undefined}
      aria-labelledby={titleId}
      data-active={active}
      className={cn(
        "relative flex h-full flex-col rounded-xl border bg-[var(--exed-card-bg)] px-7 pt-6",
        "transition-[border-color,box-shadow] duration-200 ease-out motion-reduce:transition-none",
        "has-[a:focus-visible]:ring-2 has-[a:focus-visible]:ring-[var(--exed-navy)] has-[a:focus-visible]:ring-offset-2",
        active
          ? "border-[rgba(255,200,61,0.75)] shadow-[0_0_0_4px_rgba(255,200,61,0.14),0_1px_2px_rgba(8,37,76,0.04)] hover:shadow-[0_0_0_4px_rgba(255,200,61,0.22),0_4px_16px_rgba(8,37,76,0.08)]"
          : "border-[var(--exed-card-border)] shadow-[0_1px_2px_rgba(8,37,76,0.04)]",
        state === "done" && "hover:border-[rgba(39,161,85,0.45)] hover:shadow-[0_4px_16px_rgba(8,37,76,0.08)]",
      )}
    >
      <span
        data-testid="stage-accent"
        aria-hidden="true"
        className={cn("absolute -top-px left-7 h-[3px] w-10 rounded-b-[2px]", ACCENT[state])}
      />
      <div className="flex items-center justify-between gap-3">
        <span className={cn(OVERLINE, "text-[var(--exed-ink-500)]")}>{t("Stage {{number}}", { number })}</span>
        <span
          className={cn(
            "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-[3px]",
            "font-mono text-[11px] font-semibold uppercase tracking-[0.08em]",
            STATE_STYLE[state],
          )}
        >
          <Icon
            aria-hidden="true"
            className={cn("h-3 w-3 shrink-0", state === "in_progress" && "animate-spin motion-reduce:animate-none")}
          />
          {t(STATE_LABEL[state])}
        </span>
      </div>

      <div className="mt-3 flex items-start justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-1.5">
          <h2
            id={titleId}
            className={cn(
              "break-words text-[22px] font-semibold tracking-[-0.01em]",
              locked ? "text-[var(--exed-ink-500)]" : "text-[var(--exed-ink-900)]",
            )}
          >
            {title}
          </h2>
          <p className="break-words text-sm leading-normal text-[var(--exed-ink-500)]">{description}</p>
        </div>
        <ProgressRing value={percent} state={state} />
      </div>

      <div aria-hidden="true" className="mb-5 mt-6 h-px bg-[#eef1f5]" />

      {locked ? (
        <div className="flex items-start gap-3 rounded-lg border border-[var(--exed-gold)] bg-[#fff8e1] px-4 py-3.5">
          <TriangleAlert aria-hidden="true" className="mt-px h-[18px] w-[18px] shrink-0 text-[#8a6100]" />
          <div className="flex min-w-0 flex-col gap-1">
            {lockedReason && (
              <span className="break-words text-[15px] font-semibold text-[var(--exed-ink-900)]">{lockedReason}</span>
            )}
            {lockedHint && <span className="break-words text-[13px] text-[#6b5200]">{lockedHint}</span>}
          </div>
        </div>
      ) : metrics && metrics.length > 0 ? (
        <div data-testid="stage-key-figure" className={cn("grid gap-4", metrics.length > 1 && "grid-cols-2")}>
          {metrics.map((metric) => (
            <div key={metric.label} className="flex min-w-0 flex-col gap-1">
              <span className={cn(OVERLINE, METRIC_LABEL[metric.tone ?? "neutral"])}>{metric.label}</span>
              <span className="flex flex-wrap items-baseline gap-x-2">
                <span
                  className={cn(
                    "text-5xl font-bold leading-[1.05] tracking-[-0.03em] tabular-nums",
                    // Verde só no número grande: 48px negrito passa no 3:1 de texto grande.
                    metric.tone === "success" ? "text-[var(--exed-success)]" : "text-[var(--exed-ink-900)]",
                  )}
                >
                  {metric.value}
                </span>
                {metric.suffix && <span className="text-lg text-[var(--exed-ink-500)]"> {metric.suffix}</span>}
              </span>
            </div>
          ))}
        </div>
      ) : note ? (
        <p data-testid="stage-key-figure" className="break-words text-lg text-[var(--exed-ink-500)]">{note}</p>
      ) : null}

      {progressLabel && (
        <div className="mt-[18px] flex flex-col gap-2">
          <div aria-hidden="true" className="h-1.5 overflow-hidden rounded-full bg-[#eef1f5]">
            <div
              className="h-full min-w-[6px] rounded-full bg-[var(--exed-gold)] transition-[width] duration-500 motion-reduce:transition-none"
              style={{ width: `${percent}%`, minWidth: percent === 0 ? 0 : undefined }}
            />
          </div>
          <span className="text-[13px] text-[var(--exed-ink-500)]">{progressLabel}</span>
        </div>
      )}

      <div aria-hidden="true" className="min-h-2 flex-1" />
      {locked ? (
        <div
          aria-hidden="true"
          className="mt-6 flex items-center gap-2.5 border-t border-[#eef1f5] pb-[18px] pt-4 text-sm font-semibold text-[#8a97ab]"
        >
          <Lock aria-hidden="true" className="h-4 w-4" />
          {t("Locked")}
        </div>
      ) : (
        <div className="mt-6 flex items-center gap-2.5 border-t border-[#eef1f5] pb-[18px] pt-4 text-sm font-semibold text-[var(--exed-ink-900)]">
          {ActionIcon && (
            <ActionIcon aria-hidden="true" className={cn("h-4 w-4 shrink-0", active && "text-[#b07d00]")} />
          )}
          {/* O link cobre o cartão inteiro (after:inset-0); o foco aparece no próprio cartão. */}
          <Link to={to} className="outline-none after:absolute after:inset-0 after:rounded-xl after:content-['']">
            {actionLabel}
          </Link>
          <span
            data-testid="stage-arrow"
            aria-hidden="true"
            className={cn(
              "ml-auto flex shrink-0 items-center justify-center",
              active && "h-7 w-7 rounded-full bg-[var(--exed-gold)]",
            )}
          >
            <ArrowRight aria-hidden="true" className="h-4 w-4" />
          </span>
        </div>
      )}
    </article>
  );
}
